// DocumentsBrowser.js
// Full-screen filterable document browser. Reusable for both consultant and
// staff views. Shares file data with the existing bubble UI (same file_exchange
// rows, same folder groupings) but adds filters + multi-property + bulk
// download + staff admin actions.
//
// Props:
//   scope:       { type, id? | ids?, consultant_firm?, client_id?, property_id?, parcel_id? }
//   viewer:      { role: 'consultant' | 'staff', consultant_firm?, user_name, user_id }
//   onClose:     () => void
//   initialFilters: { types?, folders?, years?, propertyIds?, clientIds?, uploadedSince? } — optional;
//                   types / folders are KEYS (doc_types.key / doc_folders.key), not names
//
// Shape contract for rendered rows (each file_exchange row):
//   id, file_name, folder_key, year_subfolder, type_key, storage_path,
//   client_id, property_id, parcel_id, viewable_consultant, viewable_client,
//   need_review, filed_at, filed_by, created_at, file_size_bytes

import React, { useState, useEffect, useMemo } from 'react';
import { supabase } from '../supabase';
import { fileUrl } from '../utils/fileUrl';
import { loadDocVocab } from '../utils/docVocab';
import { useSkipNotes, skipTooltip } from '../utils/fileWithoutScanning';
import { backdropProps } from '../utils/backdropClose';
import { useYearRange } from '../constants/yearRange';

// Grouping key for docs with NO property. A bucket keyed this way holds CLIENT-LEVEL documents, which no
// `properties` row can ever match — so every place that resolves a bucket (or a client) back to its files
// must go through these helpers, never `f.property_id === propId` or `clientPropIds.includes(f.property_id)`.
// Get that wrong and the files vanish between the query and the render, with the counts still saying they
// are there (that was the f9ee0ec half-fix: query returned 3, the client row rendered 2).
const NO_PROP_KEY = '_unknown';
// ONE label for the client-level bucket — UI headings and zip folder names read from this, so they can't
// drift apart. Distinct from "Unknown": no property is a fact about the doc, not a failed lookup.
const NO_PROP_LABEL = 'Client level (no property)';
const belongsToClient = (f, clientId, clientPropIds) =>
  clientPropIds.includes(f.property_id) || (!f.property_id && f.client_id === clientId);

// PostgREST .or() list values must be quoted — property/client ids are TEXT and may contain , ( ) or "
const orVal = v => `"${String(v).replace(/["\\]/g, m => '\\' + m)}"`;

// Folders and types come from the table (doc_folders / doc_types, utils/docVocab): rows are grouped and filtered by
// folder_key / type_key, shown by the table's current name, and a folder's has_years decides the /<year> level in
// the zip export. This replaced a hardcoded YEAR_FOLDERS list that had to be kept in step with three others.
// Year filter options come from folder_year_config via useYearRange (see yearRange.js) — this literal
// was one of seven copies of the same span, none of which agreed with the tax-bill screens.

const UPLOADED_SINCE_PRESETS = [
  { label: 'Any time', value: null },
  { label: 'Last 7 days', value: 7 },
  { label: 'Last 30 days', value: 30 },
  { label: 'Last 90 days', value: 90 },
];

// Threshold warnings for bulk download size
const WARN_FILE_COUNT = 100;
const WARN_SIZE_BYTES = 500 * 1024 * 1024; // 500 MB

export default function DocumentsBrowser({ scope, viewer, onClose, initialFilters = {} }) {
  const { years: YEARS_AVAILABLE } = useYearRange();
  const isDark = document?.body?.classList?.contains('wh-dark') || false;
  const isStaff = viewer?.role === 'staff';

  // Theme tokens — match ConsultantPortal / TaskEngine
  const c = {
    bg: isDark ? '#0f1729' : 'var(--bg)',
    header: isDark ? '#151f35' : '#ffffff',
    card: isDark ? 'rgba(255,255,255,0.04)' : '#ffffff',
    border: isDark ? 'rgba(255,255,255,0.1)' : 'var(--line)',
    text: isDark ? '#e8eaf2' : 'var(--ink)',
    muted: isDark ? '#8892aa' : 'var(--slate)',
    sub: isDark ? '#a8b0cc' : '#4a5068',
    gold: 'var(--red)',
    inputBg: isDark ? 'rgba(255,255,255,0.04)' : '#ffffff',
    inputBorder: isDark ? 'rgba(255,255,255,0.15)' : 'var(--line)',
    headerBg: isDark ? 'rgba(26,34,56,0.8)' : '#fafbfd',
    filterBg: isDark ? 'rgba(255,255,255,0.02)' : 'var(--line-soft)',
    rowBg: isDark ? 'rgba(255,255,255,0.03)' : '#ffffff',
    rowHover: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(180,0,0,0.04)',
  };

  // ==========================================================================
  // STATE — filters
  // ==========================================================================
  const [filterPropertyIds, setFilterPropertyIds] = useState(initialFilters.propertyIds || []);
  const [filterParcelIds, setFilterParcelIds]     = useState(initialFilters.parcelIds || []);
  const [filterClientIds, setFilterClientIds]     = useState(initialFilters.clientIds || []);
  const [filterConsultantFirms, setFilterConsultantFirms] = useState(initialFilters.consultantFirms || []);
  const [filterTypes, setFilterTypes]             = useState(initialFilters.types || []);
  const [filterYears, setFilterYears]             = useState(initialFilters.years || []);
  const [filterFolders, setFilterFolders]         = useState(initialFilters.folders || []);
  const [filterUploadedSince, setFilterUploadedSince] = useState(initialFilters.uploadedSince || null);
  const [searchQuery, setSearchQuery]             = useState('');  // free-text filename search

  // ==========================================================================
  // STATE — grouping + selection + data
  // ==========================================================================
  const [groupBy, setGroupBy] = useState(isStaff ? 'client' : 'property'); // 'client' | 'property' | 'parcel' | 'folder'
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [files, setFiles] = useState([]);         // all files in scope (unfiltered)
  const skipNotes = useSkipNotes(files);          // who skipped extraction, when, and why — the "not scanned" tooltip
  const [properties, setProperties] = useState([]); // all properties in scope (for filter dropdown + display)
  const [parcels, setParcels] = useState([]);       // all parcels in scope (for filter dropdown + display)
  const [clients, setClients] = useState([]);     // all clients in scope (staff only)
  const [consultantFirms, setConsultantFirms] = useState([]); // staff only
  const [docTypes, setDocTypes] = useState([]);   // distinct type_keys across scope
  const [vocab, setVocab] = useState(null);
  const folderNameOf = (key) => (key && vocab?.folder(key)?.name) || 'Other';

  // Busy states for downloads
  const [downloadBusy, setDownloadBusy] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState({ current: 0, total: 0, label: '' });

  // Per-property inline "download all" busy
  const [propertyDownloadBusy, setPropertyDownloadBusy] = useState({});

  // ==========================================================================
  // DATA LOADING — runs on mount + when scope changes
  // ==========================================================================
  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(scope)]);

  async function loadAll() {
    setLoading(true);
    setError('');
    try {
      setVocab(await loadDocVocab());   // throws (shown as the load error) if the folder table can't be read
      // Step 1: Resolve properties in scope
      let propIds = [];
      let propsData = [];
      let clientIds = [];

      if (scope.type === 'property') {
        const { data } = await supabase.from('properties')
          .select('id, master_property_id, property_name:name, client_id, consultant_firm')
          .eq('id', scope.id).limit(1);
        propsData = data || [];
        propIds = propsData.map(p => p.id);
      } else if (scope.type === 'properties') {
        const { data } = await supabase.from('properties')
          .select('id, master_property_id, property_name:name, client_id, consultant_firm')
          .in('id', scope.ids || []);
        propsData = data || [];
        propIds = propsData.map(p => p.id);
      } else if (scope.type === 'parcel') {
        const { data: parcel } = await supabase.from('parcels')
          .select('id, property_id').eq('id', scope.id).single();
        if (parcel?.property_id) {
          const { data } = await supabase.from('properties')
            .select('id, master_property_id, property_name:name, client_id, consultant_firm')
            .eq('id', parcel.property_id).limit(1);
          propsData = data || [];
          propIds = propsData.map(p => p.id);
        }
      } else if (scope.type === 'consultant') {
        // All properties belonging to this consultant firm
        const { data } = await supabase.from('properties')
          .select('id, master_property_id, property_name:name, client_id, consultant_firm')
          .eq('consultant_firm', scope.consultant_firm || viewer?.consultant_firm);
        propsData = data || [];
        propIds = propsData.map(p => p.id);
      } else if (scope.type === 'client') {
        const { data } = await supabase.from('properties')
          .select('id, master_property_id, property_name:name, client_id, consultant_firm')
          .eq('client_id', scope.client_id);
        propsData = data || [];
        propIds = propsData.map(p => p.id);
      } else if (scope.type === 'all') {
        // Staff full portfolio view
        const { data } = await supabase.from('properties')
          .select('id, master_property_id, property_name:name, client_id, consultant_firm');
        propsData = data || [];
        propIds = propsData.map(p => p.id);
      }

      setProperties(propsData);

      // Step 2: CLIENT SCOPE + names. ⛔ Deriving clientIds from propsData means a client with NO
      // properties contributes no id, so their client-level documents (a contract filed during
      // onboarding, before the property import) are unreachable — the browser is empty exactly when
      // it shouldn't be. The client axis therefore has its OWN source, independent of properties:
      //   'client'  → the id we were handed.
      //   'all'     → the clients table itself (small; also supplies the names below).
      //   property / properties / parcel / consultant → genuinely property-shaped scopes, so those
      //             stay derived from propsData: widening them to every client would widen the LENS,
      //             not just repair it.
      if (scope.type === 'client' && scope.client_id) {
        clientIds = [scope.client_id];
      } else if (scope.type === 'all' && isStaff) {
        // isStaff-gated: 'all' is the staff portfolio view. A non-staff viewer reaching it must NOT have
        // the client axis widened to every client — they fall through to the property-derived list below.
        const { data: allClientRows } = await supabase.from('clients').select('id, client_name:name');
        clientIds = (allClientRows || []).map(cl => cl.id).filter(Boolean);
        setClients(allClientRows || []);
      } else {
        clientIds = Array.from(new Set(propsData.map(p => p.client_id).filter(Boolean)));
      }

      // Names for every scope that didn't already load them above. No length guard: a scope whose
      // clients resolve to none simply fetches nothing, and leaving `clients` stale would relabel
      // rows with a previous scope's names.
      if (isStaff && !(scope.type === 'all')) {
        const { data: cData } = clientIds.length
          ? await supabase.from('clients').select('id, client_name:name').in('id', clientIds)
          : { data: [] };
        setClients(cData || []);
      }
      if (isStaff) {
        const firms = Array.from(new Set(propsData.map(p => p.consultant_firm).filter(Boolean))).sort();
        setConsultantFirms(firms);
      }

      // Step 3: Load parcels for these properties (for parcel filter + display)
      if (propIds.length > 0) {
        const { data: parcelsData } = await supabase.from('parcels')
          .select('id, account_number, assessor, property_id, client_name')
          .in('property_id', propIds);
        setParcels(parcelsData || []);
      } else {
        setParcels([]);
      }

      // Step 4: Load files. Bail ONLY when there is nothing to query on EITHER axis — a client with no
      // properties still has a client axis, and returning here was what hid their documents.
      if (propIds.length === 0 && clientIds.length === 0) {
        setFiles([]);
        setDocTypes([]);
        setLoading(false);
        return;
      }

      let q = supabase.from('file_exchange')
        .select('id, file_name, folder_key, year_subfolder, type_key, storage_path, client_id, property_id, parcel_id, viewable_consultant, viewable_client, need_review, filed_at, filed_by, extraction_skipped_at, created_at, file_size_bytes')
        .eq('need_review', false);            // exclude intake queue
      // THREE shapes, because the two axes are independent. Client-level docs (property_id NULL) can
      // never match property_id IN (…) — SQL NULL never does — so they need their own arm; and a client
      // with no properties has no property arm at all, which is the case that used to return nothing.
      if (propIds.length && clientIds.length) {
        q = q.or(`property_id.in.(${propIds.map(orVal).join(',')}),and(property_id.is.null,client_id.in.(${clientIds.map(orVal).join(',')}))`);
      } else if (propIds.length) {
        q = q.in('property_id', propIds);                          // property-shaped scope, no clients resolved
      } else {
        q = q.is('property_id', null).in('client_id', clientIds);  // clients only — plain filters, no orVal quoting needed
      }
      // Exclude soft-deleted (from Gap 2)
      q = q.not('file_name', 'like', '[REMOVED%');
      // Consultant scope restriction: only show files explicitly marked visible
      if (!isStaff) {
        q = q.eq('viewable_consultant', true);
      }
      const { data: fileData, error: fileErr } = await q.order('created_at', { ascending: false });
      if (fileErr) throw new Error(fileErr.message);

      setFiles(fileData || []);
      const types = Array.from(new Set((fileData || []).map(f => f.type_key).filter(Boolean)));
      setDocTypes(types);
    } catch (e) {
      console.error('[DocumentsBrowser loadAll]', e);
      setError(e.message || 'Failed to load documents');
    }
    setLoading(false);
  }

  // ==========================================================================
  // FILTERING — applies filter state to the loaded files
  // ==========================================================================
  const filteredFiles = useMemo(() => {
    let out = files;
    if (filterPropertyIds.length > 0) out = out.filter(f => filterPropertyIds.includes(f.property_id));
    if (filterParcelIds.length > 0)   out = out.filter(f => filterParcelIds.includes(f.parcel_id));
    if (filterClientIds.length > 0)   out = out.filter(f => filterClientIds.includes(f.client_id));
    if (filterConsultantFirms.length > 0) {
      const firmPropIds = new Set(properties.filter(p => filterConsultantFirms.includes(p.consultant_firm)).map(p => p.id));
      out = out.filter(f => firmPropIds.has(f.property_id));
    }
    if (filterTypes.length > 0)   out = out.filter(f => filterTypes.includes(f.type_key));
    if (filterYears.length > 0)   out = out.filter(f => filterYears.includes(f.year_subfolder));
    if (filterFolders.length > 0) out = out.filter(f => filterFolders.includes(f.folder_key));
    if (filterUploadedSince) {
      const cutoff = Date.now() - (filterUploadedSince * 24 * 60 * 60 * 1000);
      out = out.filter(f => new Date(f.created_at).getTime() >= cutoff);
    }
    if (searchQuery && searchQuery.trim().length > 0) {
      const q = searchQuery.trim().toLowerCase();
      out = out.filter(f => (f.file_name || '').toLowerCase().includes(q));
    }
    return out;
  }, [files, filterPropertyIds, filterParcelIds, filterClientIds, filterConsultantFirms, filterTypes, filterYears, filterFolders, filterUploadedSince, searchQuery, properties]);

  // ==========================================================================
  // GROUPING — organize filtered files for render
  // ==========================================================================
  const grouped = useMemo(() => {
    const result = {};
    const propMap = Object.fromEntries(properties.map(p => [p.id, p]));
    const clientMap = Object.fromEntries(clients.map(cl => [cl.id, cl]));

    filteredFiles.forEach(f => {
      const prop = propMap[f.property_id];
      if (groupBy === 'client') {
        // f.client_id is the fallback for a client-level row (no property to resolve the client through).
        // Reliable: both filing paths refuse to write without a client (DocumentIntake:730, TaskEngine:676).
        const clientId = prop?.client_id || f.client_id || '_unknown';
        const clientName = clientMap[clientId]?.client_name || 'Unknown Client';
        if (!result[clientId]) result[clientId] = { label: clientName, clientId, properties: {} };
        const pId = f.property_id || NO_PROP_KEY;
        if (!result[clientId].properties[pId]) result[clientId].properties[pId] = { label: prop?.property_name || (f.property_id ? 'Unknown' : NO_PROP_LABEL), propertyId: pId, folders: {} };
        const fName = f.folder_key || '_none';
        if (!result[clientId].properties[pId].folders[fName]) result[clientId].properties[pId].folders[fName] = [];
        result[clientId].properties[pId].folders[fName].push(f);
      } else if (groupBy === 'property') {
        const pId = f.property_id || NO_PROP_KEY;
        if (!result[pId]) result[pId] = { label: prop?.property_name || (f.property_id ? 'Unknown' : NO_PROP_LABEL), propertyId: pId, folders: {} };
        const fName = f.folder_key || '_none';
        if (!result[pId].folders[fName]) result[pId].folders[fName] = [];
        result[pId].folders[fName].push(f);
      } else if (groupBy === 'parcel') {
        // Bucket files by parcel_id. Files without a parcel_id go to 'Property-level (no parcel)'.
        const parcelMap = Object.fromEntries(parcels.map(p => [p.id, p]));
        const pcl = f.parcel_id ? parcelMap[f.parcel_id] : null;
        const key = f.parcel_id || '_property_level';
        const label = pcl ? `#${pcl.account_number}${pcl.assessor ? ' · ' + pcl.assessor : ''}` : 'Property-level (no parcel)';
        if (!result[key]) result[key] = { label, parcelId: f.parcel_id || null, propertyId: f.property_id, folders: {} };
        const fName = f.folder_key || '_none';
        if (!result[key].folders[fName]) result[key].folders[fName] = [];
        result[key].folders[fName].push(f);
      } else {
        // groupBy === 'folder'
        const fName = f.folder_key || '_none';
        if (!result[fName]) result[fName] = { label: folderNameOf(fName), folderName: fName, files: [] };
        result[fName].files.push(f);
      }
    });
    return result;
  }, [filteredFiles, groupBy, properties, clients, parcels]);

  // ==========================================================================
  // SELECTION HELPERS
  // ==========================================================================
  function toggleSelected(fileId) {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(fileId)) next.delete(fileId);
      else next.add(fileId);
      return next;
    });
  }
  function toggleSelectAll() {
    setSelectedIds(prev => {
      const allIds = filteredFiles.map(f => f.id);
      const allSelected = allIds.every(id => prev.has(id));
      if (allSelected) return new Set();
      return new Set(allIds);
    });
  }
  // Takes the bucket's FILES, not its key — the client-level bucket's key matches no property_id.
  function toggleSelectProperty(propFiles, checked) {
    setSelectedIds(prev => {
      const next = new Set(prev);
      propFiles.forEach(f => { checked ? next.add(f.id) : next.delete(f.id); });
      return next;
    });
  }
  function toggleSelectClient(clientId, checked) {
    const clientPropIds = properties.filter(p => p.client_id === clientId).map(p => p.id);
    const clientFiles = filteredFiles.filter(f => belongsToClient(f, clientId, clientPropIds));
    setSelectedIds(prev => {
      const next = new Set(prev);
      clientFiles.forEach(f => { checked ? next.add(f.id) : next.delete(f.id); });
      return next;
    });
  }

  // ==========================================================================
  // DOWNLOAD HELPERS
  // ==========================================================================

  // Single file download — signed URL → FETCH TO A BLOB → object URL → anchor click.
  // ⛔ THE FETCH IS NOT OPTIONAL. `a.download` is ignored on a CROSS-ORIGIN href, and an R2 signed URL is
  //    cross-origin — so setting a.href to it directly saved the file under the last segment of the R2
  //    KEY, not under file_name. The key embeds the upload-time name, so the two matched by accident and
  //    nobody noticed. They stop matching the moment a file is renamed in Intake: this button would keep
  //    saving the OLD name while every other download button saved the new one, which reads as the rename
  //    having failed. A same-origin blob: URL honours the attribute. Pattern from FileExchange.js:324;
  //    same class of bug as StaffPortal.js:2616/:2687, which are still on the broken shape.
  async function downloadSingleFile(file) {
    if (!file?.storage_path) return;
    try {
      const signedUrl = await fileUrl(file.storage_path, 'download');
      const resp = await fetch(signedUrl);
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      if (!blob.size) throw new Error('the stored file came back empty (0 bytes)');
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objUrl;
      a.download = file.file_name;
      a.click();
      setTimeout(() => URL.revokeObjectURL(objUrl), 10000);
    } catch (err) {
      // The old version returned silently on a null URL — a click that did nothing, with nothing to report.
      alert(`Could not download "${file.file_name}": ${err.message}`);
    }
  }

  // Single file preview — signed URL → new tab
  async function previewSingleFile(file) {
    if (!file?.storage_path) return;
    const url = await fileUrl(file.storage_path, 'open').catch(() => null);
    if (url) window.open(url, '_blank', 'noopener,noreferrer');
  }

  // Bulk ZIP download using JSZip (lazy-loaded via CDN since JSZip may not be in npm bundle)
  async function downloadZip(filesToZip, zipLabel) {
    if (!filesToZip || filesToZip.length === 0) {
      setError('No files to download.');
      return;
    }

    // Size check
    const totalBytes = filesToZip.reduce((acc, f) => acc + (f.file_size_bytes || 0), 0);
    if (filesToZip.length > WARN_FILE_COUNT || totalBytes > WARN_SIZE_BYTES) {
      const sizeMB = Math.round(totalBytes / 1024 / 1024);
      const confirm = window.confirm(
        `You're about to download ${filesToZip.length} files (~${sizeMB} MB). Large ZIPs can take a few minutes and may time out. Continue?`
      );
      if (!confirm) return;
    }

    setDownloadBusy(true);
    setDownloadProgress({ current: 0, total: filesToZip.length, label: 'Preparing...' });

    try {
      // Lazy-load JSZip from CDN (so it's only fetched when someone actually downloads)
      if (!window.JSZip) {
        await new Promise((res, rej) => {
          const script = document.createElement('script');
          script.src = 'https://cdnjs.cloudflare.com/ajax/libs/jszip/3.10.1/jszip.min.js';
          script.onload = res;
          script.onerror = () => rej(new Error('JSZip CDN failed to load'));
          document.head.appendChild(script);
        });
      }
      const JSZip = window.JSZip;
      const zip = new JSZip();
      const propMap = Object.fromEntries(properties.map(p => [p.id, p]));

      // Track filename collisions per folder path so we never overwrite inside the zip
      const usedPaths = new Set();

      for (let i = 0; i < filesToZip.length; i++) {
        const f = filesToZip[i];
        setDownloadProgress({ current: i + 1, total: filesToZip.length, label: f.file_name });
        if (!f.storage_path) continue;

        // Build path: <Property>/<Folder>[/<Year>]/filename
        // Same label the UI shows: a client-level doc has NO property, which is not the same thing as a
        // property that failed to resolve ("Unknown Property" would put both in one folder and imply loss).
        const propName = (propMap[f.property_id]?.property_name
          || (f.property_id ? 'Unknown Property' : NO_PROP_LABEL)).replace(/[<>:"/\\|?*]/g, '_');
        // Zip paths use the folder's display NAME — a download reads /Appeals/. Safe: nothing parses folder names
        // back out of a path, and storage keys never embed this folder. The year level follows has_years.
        const folderName = folderNameOf(f.folder_key).replace(/[<>:"/\\|?*]/g, '_');
        const year = f.year_subfolder;
        const isYearFolder = !!vocab?.folder(f.folder_key)?.has_years && year;
        let path = `${propName}/${folderName}`;
        if (isYearFolder) path += `/${year}`;

        // Handle filename collisions — append _2, _3, etc.
        let safeName = f.file_name.replace(/[<>:"/\\|?*]/g, '_');
        let fullPath = `${path}/${safeName}`;
        let counter = 2;
        while (usedPaths.has(fullPath)) {
          const extMatch = safeName.match(/^(.*)(\.[^.]+)$/);
          const base = extMatch ? extMatch[1] : safeName;
          const ext = extMatch ? extMatch[2] : '';
          fullPath = `${path}/${base}_${counter}${ext}`;
          counter++;
        }
        usedPaths.add(fullPath);

        try {
          const url = await fileUrl(f.storage_path, 'download').catch(() => null);
          if (url) {
            const resp = await fetch(url);
            if (resp.ok) {
              const blob = await resp.blob();
              zip.file(fullPath, blob);
            }
          }
        } catch (innerErr) {
          console.warn('[downloadZip] file fetch failed, skipping:', f.file_name, innerErr);
        }
      }

      setDownloadProgress({ current: filesToZip.length, total: filesToZip.length, label: 'Building ZIP...' });
      const zipBlob = await zip.generateAsync({ type: 'blob', compression: 'DEFLATE', compressionOptions: { level: 6 } });
      const today = new Date().toISOString().slice(0, 10);
      const zipName = `${zipLabel || 'Documents'}_${today}.zip`.replace(/[<>:"/\\|?*]/g, '_');

      // Trigger download
      const url = URL.createObjectURL(zipBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = zipName;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) {
      console.error('[downloadZip]', e);
      setError('ZIP download failed: ' + (e.message || 'unknown'));
    }
    setDownloadBusy(false);
    setDownloadProgress({ current: 0, total: 0, label: '' });
  }

  // ==========================================================================
  // FILTER-AWARE vs UNFILTERED download button actions
  // ==========================================================================

  // Green: download what's currently checked
  async function downloadSelected() {
    const toZip = files.filter(f => selectedIds.has(f.id));
    await downloadZip(toZip, `Documents_selected_${toZip.length}`);
  }

  // Blue: download every file that matches current filters
  async function downloadAllFiltered() {
    await downloadZip(filteredFiles, `Documents_filtered_${filteredFiles.length}`);
  }

  // Amber: download every file in scope, ignoring filters
  async function downloadAllInScope() {
    await downloadZip(files, `Documents_all_${files.length}`);
  }

  // Amber per-property: download every file for ONE property, ignoring filters
  // Takes the bucket's FILES + label, not just its key — see toggleSelectProperty.
  async function downloadAllForProperty(propertyId, propFiles, propName = 'Property') {
    setPropertyDownloadBusy(prev => ({ ...prev, [propertyId]: true }));
    await downloadZip(propFiles, `Documents_${propName.replace(/\s+/g, '_')}`);
    setPropertyDownloadBusy(prev => ({ ...prev, [propertyId]: false }));
  }

  // Amber per-client (staff only): download every file for ONE client
  async function downloadAllForClient(clientId) {
    const clientPropIds = properties.filter(p => p.client_id === clientId).map(p => p.id);
    const clientFiles = files.filter(f => belongsToClient(f, clientId, clientPropIds));
    const clientName = clients.find(cl => cl.id === clientId)?.client_name || 'Client';
    await downloadZip(clientFiles, `Documents_${clientName.replace(/\s+/g, '_')}`);
  }

  // ==========================================================================
  // STYLE TOKENS (inline)
  // ==========================================================================
  const s = {
    overlay: {
      position: 'fixed', inset: 0, zIndex: 9500,
      background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(2px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '24px', fontFamily: "'Public Sans',system-ui,sans-serif",
    },
    panel: {
      background: c.header, borderRadius: '14px', border: `1px solid ${c.border}`,
      width: '100%', maxWidth: '1380px', height: '92vh', display: 'flex', flexDirection: 'column',
      boxShadow: '0 10px 40px rgba(0,0,0,0.3)', overflow: 'hidden',
    },
    topBar: {
      padding: '14px 18px', borderBottom: `1px solid ${c.border}`, background: c.headerBg,
      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '14px',
    },
    filterBar: {
      padding: '12px 18px', borderBottom: `1px solid ${c.border}`, background: c.filterBg,
    },
    filterGrid: {
      display: 'grid',
      gridTemplateColumns: `repeat(auto-fill, minmax(${isStaff ? '160px' : '180px'}, 1fr))`,
      gap: '10px', marginBottom: '10px',
    },
    filterCol: {
      display: 'flex', flexDirection: 'column', gap: '4px',
    },
    filterLabel: {
      fontSize: '10px', fontWeight: '700', textTransform: 'uppercase',
      letterSpacing: '0.06em', color: c.muted,
    },
    filterSelect: {
      padding: '6px 8px', borderRadius: '6px', fontSize: '12px',
      border: `1px solid ${c.inputBorder}`, background: c.inputBg, color: c.text,
      fontFamily: "'Public Sans',system-ui,sans-serif", cursor: 'pointer',
    },
    chip: (bg, fg, border) => ({
      display: 'inline-flex', alignItems: 'center', gap: '4px',
      padding: '3px 10px', background: bg, color: fg, border: `1px solid ${border}`,
      borderRadius: '20px', fontSize: '11px', fontWeight: '600', cursor: 'default',
    }),
    selectAllBar: {
      padding: '10px 18px', background: c.card, borderBottom: `1px solid ${c.border}`,
      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', fontSize: '11px',
    },
    scrollArea: {
      flex: 1, overflowY: 'auto', padding: '14px 18px', background: c.bg,
    },
    bottomBar: {
      padding: '12px 18px', borderTop: `1px solid ${c.border}`, background: c.filterBg,
    },
    btnGreen: {
      padding: '8px 14px', background: 'rgba(34,197,94,0.12)', color: '#0F6E56',
      border: '1px solid rgba(34,197,94,0.35)', borderRadius: '7px', fontSize: '12px',
      fontWeight: '700', cursor: 'pointer', fontFamily: "'Public Sans',system-ui,sans-serif",
    },
    btnBlue: {
      padding: '8px 14px', background: 'rgba(180,0,0,0.12)', color: '#B40000',
      border: '1px solid rgba(180,0,0,0.35)', borderRadius: '7px', fontSize: '12px',
      fontWeight: '700', cursor: 'pointer', fontFamily: "'Public Sans',system-ui,sans-serif",
    },
    btnAmber: {
      padding: '8px 14px', background: 'rgba(48,48,48,0.06)', color: '#303030',
      border: '1px solid rgba(48,48,48,0.20)', borderRadius: '7px', fontSize: '12px',
      fontWeight: '700', cursor: 'pointer', fontFamily: "'Public Sans',system-ui,sans-serif",
    },
    btnAmberSm: {
      padding: '4px 10px', background: 'rgba(48,48,48,0.05)', color: '#303030',
      border: '1px solid rgba(48,48,48,0.18)', borderRadius: '6px', fontSize: '10px',
      fontWeight: '700', cursor: 'pointer', fontFamily: "'Public Sans',system-ui,sans-serif", whiteSpace: 'nowrap',
    },
    btnGhost: {
      padding: '6px 12px', background: 'transparent', color: c.sub,
      border: `1px solid ${c.inputBorder}`, borderRadius: '6px', fontSize: '11px',
      cursor: 'pointer', fontFamily: "'Public Sans',system-ui,sans-serif",
    },
  };

  // ==========================================================================
  // FILTER PILLS (render helper)
  // ==========================================================================
  function renderChips() {
    const chips = [];
    const propMap = Object.fromEntries(properties.map(p => [p.id, p]));
    const clientMap = Object.fromEntries(clients.map(cl => [cl.id, cl]));
    const parcelMap = Object.fromEntries(parcels.map(p => [p.id, p]));

    filterClientIds.forEach(id => chips.push({
      bg: '#FCEBEB', fg: '#791F1F', border: '#F09595', label: clientMap[id]?.client_name || 'Client',
      onRemove: () => setFilterClientIds(prev => prev.filter(x => x !== id)),
    }));
    filterPropertyIds.forEach(id => chips.push({
      bg: '#EEEDFE', fg: '#3C3489', border: '#AFA9EC', label: propMap[id]?.property_name || 'Property',
      onRemove: () => setFilterPropertyIds(prev => prev.filter(x => x !== id)),
    }));
    filterParcelIds.forEach(id => chips.push({
      bg: '#FDF6E3', fg: '#7A5D0F', border: '#E0C56E', label: `#${parcelMap[id]?.account_number || id}`,
      onRemove: () => setFilterParcelIds(prev => prev.filter(x => x !== id)),
    }));
    filterConsultantFirms.forEach(firm => chips.push({
      bg: '#FBEAF0', fg: '#72243E', border: '#ED93B1', label: firm,
      onRemove: () => setFilterConsultantFirms(prev => prev.filter(x => x !== firm)),
    }));
    filterFolders.forEach(f => chips.push({
      bg: '#FAEEDA', fg: '#854F0B', border: '#EF9F27', label: f,
      onRemove: () => setFilterFolders(prev => prev.filter(x => x !== f)),
    }));
    filterTypes.forEach(t => chips.push({
      bg: '#E1F5EE', fg: '#0F6E56', border: '#5DCAA5', label: t,
      onRemove: () => setFilterTypes(prev => prev.filter(x => x !== t)),
    }));
    filterYears.forEach(y => chips.push({
      bg: '#FAEEDA', fg: '#854F0B', border: '#EF9F27', label: y,
      onRemove: () => setFilterYears(prev => prev.filter(x => x !== y)),
    }));
    if (filterUploadedSince) chips.push({
      bg: '#E6F1FB', fg: '#0C447C', border: '#85B7EB', label: `Last ${filterUploadedSince}d`,
      onRemove: () => setFilterUploadedSince(null),
    });
    if (searchQuery && searchQuery.trim().length > 0) chips.push({
      bg: '#E6F1FB', fg: '#0C447C', border: '#85B7EB', label: `"${searchQuery}"`,
      onRemove: () => setSearchQuery(''),
    });

    if (chips.length === 0) return null;
    return (
      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
        {chips.map((ch, i) => (
          <span key={i} style={s.chip(ch.bg, ch.fg, ch.border)}>
            {ch.label}
            <button onClick={ch.onRemove} style={{ background: 'transparent', border: 'none', color: ch.fg, cursor: 'pointer', fontSize: '14px', lineHeight: 1, padding: 0, marginLeft: 2 }}>×</button>
          </span>
        ))}
        <button onClick={clearAllFilters} style={{ background: 'transparent', border: 'none', color: c.muted, fontSize: '11px', cursor: 'pointer', marginLeft: 4 }}>Clear all</button>
      </div>
    );
  }
  function clearAllFilters() {
    setFilterPropertyIds([]); setFilterParcelIds([]); setFilterClientIds([]); setFilterConsultantFirms([]);
    setFilterTypes([]); setFilterYears([]); setFilterFolders([]); setFilterUploadedSince(null);
    setSearchQuery('');
  }

  // ==========================================================================
  // FILTER DROPDOWN HELPERS (simple native select with multi via checkbox list)
  // ==========================================================================
  function MultiSelectDropdown({ label, options, selected, onChange }) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const inputRef = React.useRef(null);
    const oneSelectedLabel = selected.length === 1 ? (options.find(o => o.value === selected[0])?.label || selected[0]) : null;
    const display = selected.length === 0 ? 'All' : selected.length === 1 ? oneSelectedLabel : `${selected.length} selected`;
    const filteredOptions = query.trim() === '' ? options : options.filter(opt => String(opt.label || '').toLowerCase().includes(query.toLowerCase()));
    React.useEffect(() => {
      if (open && inputRef.current) inputRef.current.focus();
      if (!open) setQuery('');
    }, [open]);
    return (
      <div style={{ position: 'relative' }}>
        <button onClick={() => setOpen(v => !v)}
          style={{ ...s.filterSelect, width: '100%', textAlign: 'left', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{display}</span>
          <span style={{ marginLeft: 6, color: c.muted }}>▾</span>
        </button>
        {open && (
          <div style={{ position: 'absolute', zIndex: 20, top: '100%', left: 0, right: 0, marginTop: 2, maxHeight: '300px', display: 'flex', flexDirection: 'column', background: c.card, border: `1px solid ${c.border}`, borderRadius: '6px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
            <div style={{ padding: '6px', borderBottom: `1px solid ${c.border}`, background: c.card }}>
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder="Type to filter…"
                style={{ width: '100%', padding: '5px 8px', fontSize: '11px', border: `1px solid ${c.inputBorder}`, borderRadius: '4px', background: c.inputBg, color: c.text, boxSizing: 'border-box', outline: 'none', fontFamily: "'Public Sans',system-ui,sans-serif" }}
              />
            </div>
            <div style={{ overflowY: 'auto', flex: 1 }}>
              {filteredOptions.length === 0 && (
                <div style={{ padding: '8px 10px', fontSize: '11px', color: c.muted, fontStyle: 'italic' }}>
                  {options.length === 0 ? 'No options' : 'No matches'}
                </div>
              )}
              {filteredOptions.map(opt => {
                const isSelected = selected.includes(opt.value);
                return (
                  <label key={opt.value} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 10px', fontSize: '11px', cursor: 'pointer', color: c.text, background: isSelected ? 'rgba(180,0,0,0.08)' : 'transparent' }}>
                    <input type="checkbox" checked={isSelected} onChange={() => {
                      if (isSelected) onChange(selected.filter(x => x !== opt.value));
                      else onChange([...selected, opt.value]);
                    }} />
                    <span>{opt.label}</span>
                  </label>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  }

  // ==========================================================================
  // RENDER — file row
  // ==========================================================================
  function renderFileRow(f) {
    const isSelected = selectedIds.has(f.id);
    const isInternalOnly = isStaff && f.viewable_consultant === false;
    const isNew = filterUploadedSince === null && (Date.now() - new Date(f.created_at).getTime()) < 7 * 24 * 60 * 60 * 1000;
    // Size: show KB for <1MB, MB with 1 decimal for >=1MB
    let sizeLabel = null;
    if (f.file_size_bytes) {
      if (f.file_size_bytes < 1024 * 1024) sizeLabel = `${Math.round(f.file_size_bytes / 1024)} KB`;
      else sizeLabel = `${(f.file_size_bytes / 1024 / 1024).toFixed(1)} MB`;
    }
    // Parcel lookup — show account number if this file is parcel-level
    const parcel = f.parcel_id ? parcels.find(p => p.id === f.parcel_id) : null;
    const parcelLabel = parcel ? `Parcel #${parcel.account_number}` : null;

    return (
      <div key={f.id} style={{
        display: 'grid',
        gridTemplateColumns: isStaff
          ? '18px 18px 1fr auto auto auto auto'
          : '18px 18px 1fr auto auto',
        alignItems: 'center', gap: '8px',
        padding: '7px 10px',
        background: isSelected ? (isDark ? 'rgba(96,165,250,0.08)' : 'rgba(96,165,250,0.05)') : c.rowBg,
        borderRadius: '6px', border: `1px solid ${c.border}`,
        marginBottom: '3px',
      }}>
        <input type="checkbox" checked={isSelected} onChange={() => toggleSelected(f.id)} style={{ accentColor: '#60a5fa', cursor: 'pointer' }} />
        <span style={{ fontSize: '14px' }}>📄</span>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: '12px', fontWeight: '600', color: c.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {f.file_name}
            {isNew && <span style={{ marginLeft: 6, padding: '1px 6px', background: 'rgba(96,165,250,0.15)', color: '#60a5fa', borderRadius: '6px', fontSize: '9px', fontWeight: '700' }}>NEW</span>}
            {/* Filed without running extraction — so an empty Proposed Values panel is not read as missing data.
                Quiet on purpose: a deliberate skip is a normal state, not a fault. */}
            {f.extraction_skipped_at && <span title={skipTooltip(skipNotes[f.id])} style={{ marginLeft: 6, padding: '1px 6px', border: `1px solid ${c.border}`, color: c.muted, borderRadius: '6px', fontSize: '9px', fontWeight: 600, whiteSpace: 'nowrap' }}>not scanned</span>}
          </div>
          <div style={{ fontSize: '10px', color: c.muted, marginTop: '2px' }}>
            {f.year_subfolder || '—'} · {new Date(f.created_at).toLocaleDateString()}
            {sizeLabel && ` · ${sizeLabel}`}
            {parcelLabel && <> · <span style={{ color: isDark ? '#b5a47a' : '#956f1d', fontWeight: '600' }}>{parcelLabel}</span></>}
            {isStaff && f.filed_by && <> · Filed by <span style={{ color: '#60a5fa' }}>{f.filed_by}</span></>}
          </div>
        </div>
        {isStaff && (
          <span title={isInternalOnly ? 'Internal only — not visible to consultant' : 'Visible to consultant'}
            style={{ fontSize: '11px', color: isInternalOnly ? '#D85A30' : '#60a5fa', whiteSpace: 'nowrap' }}>
            {isInternalOnly ? '🔒' : '👁'}
          </span>
        )}
        <button onClick={() => previewSingleFile(f)}
          style={{ fontSize: '10px', color: '#60a5fa', padding: '3px 8px', border: '1px solid rgba(96,165,250,0.35)', borderRadius: '5px', background: 'transparent', cursor: 'pointer', fontFamily: "'Public Sans',system-ui,sans-serif" }}>
          👁 View
        </button>
        <button onClick={() => downloadSingleFile(f)}
          style={{ fontSize: '10px', color: '#0F6E56', padding: '3px 8px', border: '1px solid rgba(34,197,94,0.35)', borderRadius: '5px', background: 'transparent', cursor: 'pointer', fontFamily: "'Public Sans',system-ui,sans-serif" }}>
          ↓ Save
        </button>
        {isStaff && (
          <button title="Edit filing (coming soon)" disabled
            style={{ fontSize: '10px', color: '#3C3489', padding: '3px 8px', border: '1px solid rgba(175,169,236,0.4)', borderRadius: '5px', background: 'transparent', cursor: 'not-allowed', opacity: 0.5, fontFamily: "'Public Sans',system-ui,sans-serif" }}>
            ✎
          </button>
        )}
      </div>
    );
  }

  // ==========================================================================
  // RENDER — group sections
  // ==========================================================================
  function renderFolderBlock(folderKey, filesInFolder) {
    return (
      <div key={folderKey} style={{ marginBottom: '14px' }}>
        <div style={{ fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.07em', color: c.gold, marginBottom: '6px', paddingLeft: '4px' }}>
          📁 {folderNameOf(folderKey)} ({filesInFolder.length})
        </div>
        {filesInFolder.map(renderFileRow)}
      </div>
    );
  }

  function renderParcelSection(parcelKey, parcelNode) {
    // parcelKey is either the parcel UUID or '_property_level' for files with no parcel_id
    const isPropertyLevel = parcelKey === '_property_level';
    const parcel = isPropertyLevel ? null : parcels.find(p => p.id === parcelKey);
    const parcelFilesInScope = isPropertyLevel
      ? files.filter(f => !f.parcel_id)
      : files.filter(f => f.parcel_id === parcelKey);
    const parcelFilesFiltered = isPropertyLevel
      ? filteredFiles.filter(f => !f.parcel_id)
      : filteredFiles.filter(f => f.parcel_id === parcelKey);
    const allSelected = parcelFilesFiltered.length > 0 && parcelFilesFiltered.every(f => selectedIds.has(f.id));
    // Property label for context
    const propForThisParcel = parcel ? properties.find(p => p.id === parcel.property_id) : null;
    return (
      <div key={parcelKey} style={{ marginBottom: '14px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px', padding: '7px 10px',
          background: isPropertyLevel ? c.filterBg : 'rgba(180,0,0,0.08)',
          border: `1px solid ${isPropertyLevel ? c.border : 'rgba(180,0,0,0.3)'}`,
          borderLeft: isPropertyLevel ? `1px solid ${c.border}` : '3px solid #B40000',
          borderRadius: '6px' }}>
          <input type="checkbox" checked={allSelected} onChange={e => {
            setSelectedIds(prev => {
              const next = new Set(prev);
              parcelFilesFiltered.forEach(f => { e.target.checked ? next.add(f.id) : next.delete(f.id); });
              return next;
            });
          }} style={{ accentColor: '#60a5fa', cursor: 'pointer' }} />
          <div style={{ fontSize: '13px', fontWeight: '700', flex: 1, color: c.text }}>
            {parcelNode.label}
            {propForThisParcel && <span style={{ fontSize: '11px', color: c.muted, marginLeft: 8, fontWeight: '400' }}>· {propForThisParcel.property_name}</span>}
          </div>
          <span style={{ fontSize: '11px', color: c.muted }}>{parcelFilesFiltered.length} shown · {parcelFilesInScope.length} total</span>
        </div>
        <div style={{ paddingLeft: '16px' }}>
          {Object.entries(parcelNode.folders).map(([folderName, filesInFolder]) => renderFolderBlock(folderName, filesInFolder))}
        </div>
      </div>
    );
  }

  function renderPropertySection(propId, propNode, indent = 0, clientId = null) {
    const noProp = propId === NO_PROP_KEY;      // the client-level bucket — no `properties` row to find
    const prop = properties.find(p => p.id === propId);
    if (!prop && !noProp) return null;
    // Under a client, the bucket is that client's client-level docs; at top level (groupBy 'property')
    // it is every client-level doc in scope — the query already limits scope to the clients in view.
    const belongs = noProp
      ? f => !f.property_id && (!clientId || f.client_id === clientId)
      : f => f.property_id === propId;
    const bucketFiltered = filteredFiles.filter(belongs);
    const totalInScope = files.filter(belongs).length;
    const filesInFiltered = bucketFiltered.length;
    const allInPropSelected = filesInFiltered > 0 && bucketFiltered.every(f => selectedIds.has(f.id));
    const busy = !!propertyDownloadBusy[propId];
    return (
      <div key={propId} style={{ marginBottom: '14px', marginLeft: indent }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px', padding: '7px 10px', background: c.card, borderRadius: '6px', border: `1px solid ${c.border}` }}>
          <input type="checkbox" checked={allInPropSelected} onChange={e => toggleSelectProperty(bucketFiltered, e.target.checked)} style={{ accentColor: '#60a5fa', cursor: 'pointer' }} />
          <div style={{ fontSize: '13px', fontWeight: '700', flex: 1, color: c.text }}>{prop ? prop.property_name : propNode.label}</div>
          <span style={{ fontSize: '11px', color: c.muted }}>{filesInFiltered} shown · {totalInScope} total</span>
          <button style={{ ...s.btnAmberSm, opacity: busy ? 0.5 : 1 }} disabled={busy}
            onClick={() => downloadAllForProperty(propId, files.filter(belongs), prop ? prop.property_name : 'Client_level')}>
            {busy ? '…' : `↓ All ${totalInScope} for this property`}
          </button>
        </div>
        <div style={{ paddingLeft: '16px' }}>
          {Object.entries(propNode.folders).map(([folderName, filesInFolder]) => renderFolderBlock(folderName, filesInFolder))}
        </div>
      </div>
    );
  }

  function renderClientSection(clientId, clientNode) {
    const clientPropIds = properties.filter(p => p.client_id === clientId).map(p => p.id);
    const clientFiltered = filteredFiles.filter(f => belongsToClient(f, clientId, clientPropIds));
    const totalFiles = files.filter(f => belongsToClient(f, clientId, clientPropIds)).length;
    const filteredFilesCount = clientFiltered.length;
    const allSelected = filteredFilesCount > 0 && clientFiltered.every(f => selectedIds.has(f.id));
    return (
      <div key={clientId} style={{ marginBottom: '18px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px', padding: '8px 12px', background: 'rgba(83,74,183,0.1)', borderLeft: '3px solid #534AB7', borderRadius: '0 6px 6px 0' }}>
          <input type="checkbox" checked={allSelected} onChange={e => toggleSelectClient(clientId, e.target.checked)} style={{ accentColor: '#60a5fa', cursor: 'pointer' }} />
          <div style={{ fontSize: '13px', fontWeight: '700', flex: 1, color: '#303030' }}>{clientNode.label}</div>
          <span style={{ fontSize: '11px', color: c.muted }}>{clientPropIds.length} properties · {filteredFilesCount} shown · {totalFiles} total</span>
          <button style={s.btnAmberSm} onClick={() => downloadAllForClient(clientId)}>
            ↓ All {totalFiles}
          </button>
        </div>
        {Object.entries(clientNode.properties).map(([propId, propNode]) => renderPropertySection(propId, propNode, 16, clientId))}
      </div>
    );
  }

  // ==========================================================================
  // RENDER — main
  // ==========================================================================
  const scopeTitle = (() => {
    if (scope.type === 'property' && properties[0]) return `Documents — ${properties[0].property_name}`;
    if (scope.type === 'properties') return `Documents — ${properties.length} properties`;
    if (scope.type === 'parcel') return `Documents — parcel`;
    if (scope.type === 'consultant') return `Documents — ${scope.consultant_firm || viewer?.consultant_firm || 'portfolio'}`;
    if (scope.type === 'client') return `Documents — client`;
    if (scope.type === 'all') return 'Documents — all clients';
    return 'Documents';
  })();

  const propertyOptions = properties.map(p => ({ value: p.id, label: p.property_name })).sort((a, b) => String(a.label || '').localeCompare(String(b.label || '')));
  const clientOptions = clients.map(cl => ({ value: cl.id, label: cl.client_name })).sort((a, b) => String(a.label || '').localeCompare(String(b.label || '')));
  const parcelOptions = parcels.map(p => ({ value: p.id, label: `${p.account_number}${p.assessor ? ' (' + p.assessor + ')' : ''}` })).sort((a, b) => String(a.label || '').localeCompare(String(b.label || '')));
  const consultantFirmOptions = consultantFirms.map(firm => ({ value: firm, label: firm }));
  // ⛔ value = KEY, label = the table's name. A filter compares keys, so a rename never stops a chip matching its rows.
  const typeOptions = docTypes.map(k => ({ value: k, label: vocab?.type(k)?.name || k })).sort((a, b) => a.label.localeCompare(b.label));
  const folderOptions = Array.from(new Set(files.map(f => f.folder_key).filter(Boolean))).map(k => ({ value: k, label: folderNameOf(k) })).sort((a, b) => a.label.localeCompare(b.label));
  const yearOptions = YEARS_AVAILABLE.map(y => ({ value: y, label: y }));

  return (
    <div style={s.overlay} {...backdropProps(onClose)}>
      <div style={s.panel}>

        {/* TOP BAR */}
        <div style={s.topBar}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
            {isStaff && (
              <span style={{ padding: '3px 9px', background: 'rgba(83,74,183,0.15)', color: '#7F77DD', borderRadius: '6px', fontSize: '10px', fontWeight: '700', letterSpacing: '0.05em', textTransform: 'uppercase' }}>Staff</span>
            )}
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: '15px', fontWeight: '700', color: c.text, fontFamily: "'Archivo',sans-serif" }}>{scopeTitle}</div>
              <div style={{ fontSize: '11px', color: c.muted, marginTop: '2px' }}>
                {loading ? 'Loading...' : `${filteredFiles.length} of ${files.length} documents shown · ${selectedIds.size} selected`}
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: c.muted }}>
              <span>Group:</span>
              {(isStaff ? ['client', 'property', 'parcel', 'folder'] : ['property', 'parcel', 'folder']).map(opt => (
                <button key={opt} onClick={() => setGroupBy(opt)}
                  style={{ padding: '4px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: '600', textTransform: 'capitalize', cursor: 'pointer', fontFamily: "'Public Sans',system-ui,sans-serif",
                    background: groupBy === opt ? 'rgba(96,165,250,0.15)' : 'transparent',
                    color: groupBy === opt ? '#60a5fa' : c.sub,
                    border: `1px solid ${groupBy === opt ? 'rgba(96,165,250,0.35)' : c.inputBorder}` }}>
                  {opt}
                </button>
              ))}
            </div>
            <button onClick={onClose} style={{ padding: '6px 12px', background: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.3)', borderRadius: '6px', color: '#f87171', fontSize: '12px', fontWeight: '600', cursor: 'pointer', fontFamily: "'Public Sans',system-ui,sans-serif" }}>✕ Close</button>
          </div>
        </div>

        {/* FILTER BAR */}
        <div style={s.filterBar}>
          <div style={s.filterGrid}>
            {isStaff && (
              <div style={s.filterCol}>
                <div style={s.filterLabel}>Clients</div>
                <MultiSelectDropdown options={clientOptions} selected={filterClientIds} onChange={setFilterClientIds} />
              </div>
            )}
            {isStaff && (
              <div style={s.filterCol}>
                <div style={s.filterLabel}>Consultant</div>
                <MultiSelectDropdown options={consultantFirmOptions} selected={filterConsultantFirms} onChange={setFilterConsultantFirms} />
              </div>
            )}
            <div style={s.filterCol}>
              <div style={s.filterLabel}>Properties</div>
              <MultiSelectDropdown options={propertyOptions} selected={filterPropertyIds} onChange={setFilterPropertyIds} />
            </div>
            <div style={s.filterCol}>
              <div style={s.filterLabel}>Parcels</div>
              <MultiSelectDropdown options={parcelOptions} selected={filterParcelIds} onChange={setFilterParcelIds} />
            </div>
            <div style={s.filterCol}>
              <div style={s.filterLabel}>Folder</div>
              <MultiSelectDropdown options={folderOptions} selected={filterFolders} onChange={setFilterFolders} />
            </div>
            <div style={s.filterCol}>
              <div style={s.filterLabel}>Document type</div>
              <MultiSelectDropdown options={typeOptions} selected={filterTypes} onChange={setFilterTypes} />
            </div>
            <div style={s.filterCol}>
              <div style={s.filterLabel}>Year</div>
              <MultiSelectDropdown options={yearOptions} selected={filterYears} onChange={setFilterYears} />
            </div>
            <div style={s.filterCol}>
              <div style={s.filterLabel}>Uploaded since</div>
              <select value={filterUploadedSince || ''} onChange={e => setFilterUploadedSince(e.target.value ? Number(e.target.value) : null)}
                style={s.filterSelect}>
                {UPLOADED_SINCE_PRESETS.map(p => <option key={p.label} value={p.value || ''}>{p.label}</option>)}
              </select>
            </div>
            <div style={s.filterCol}>
              <div style={s.filterLabel}>Search filename</div>
              <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                placeholder="e.g. rent roll 2025"
                style={{ ...s.filterSelect, cursor: 'text' }} />
            </div>
          </div>
          {renderChips()}
        </div>

        {/* SELECT ALL BAR */}
        <div style={s.selectAllBar}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', color: c.sub }}>
            <input type="checkbox" checked={filteredFiles.length > 0 && filteredFiles.every(f => selectedIds.has(f.id))} onChange={toggleSelectAll} style={{ accentColor: '#60a5fa' }} />
            Select all {filteredFiles.length} visible
          </label>
          {isStaff && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '10px', color: c.muted }}>
              <span><span style={{ color: '#60a5fa' }}>👁</span> Consultant visible</span>
              <span><span style={{ color: '#D85A30' }}>🔒</span> Internal only</span>
            </div>
          )}
        </div>

        {/* SCROLL AREA — grouped results */}
        <div style={s.scrollArea}>
          {loading && <div style={{ padding: '40px', textAlign: 'center', color: c.muted, fontSize: '13px' }}>⏳ Loading documents…</div>}
          {error && <div style={{ padding: '20px', background: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.3)', borderRadius: '8px', color: '#f87171', fontSize: '12px', marginBottom: '12px' }}>{error}</div>}
          {!loading && !error && filteredFiles.length === 0 && (
            <div style={{ padding: '60px 20px', textAlign: 'center', color: c.muted, fontSize: '13px' }}>
              No documents match the current filters.
              {(filterPropertyIds.length || filterClientIds.length || filterTypes.length || filterYears.length || filterFolders.length || filterUploadedSince) > 0 && (
                <div style={{ marginTop: '12px' }}>
                  <button onClick={clearAllFilters} style={s.btnGhost}>Clear all filters</button>
                </div>
              )}
            </div>
          )}
          {!loading && !error && filteredFiles.length > 0 && (
            <>
              {groupBy === 'client' && Object.entries(grouped).map(([cid, node]) => renderClientSection(cid, node))}
              {groupBy === 'property' && Object.entries(grouped).map(([pid, node]) => renderPropertySection(pid, node, 0))}
              {groupBy === 'parcel' && Object.entries(grouped).map(([pkey, node]) => renderParcelSection(pkey, node))}
              {groupBy === 'folder' && Object.entries(grouped).map(([folderName, node]) => renderFolderBlock(folderName, node.files))}
            </>
          )}
        </div>

        {/* BOTTOM BAR — download actions */}
        <div style={s.bottomBar}>
          {downloadBusy && (
            <div style={{ marginBottom: '10px', padding: '8px 12px', background: 'rgba(96,165,250,0.1)', border: '1px solid rgba(96,165,250,0.3)', borderRadius: '6px', fontSize: '11px', color: c.text }}>
              Building ZIP… {downloadProgress.current} of {downloadProgress.total} {downloadProgress.label && `· ${downloadProgress.label}`}
            </div>
          )}
          <div style={{ fontSize: '11px', color: c.muted, marginBottom: '10px' }}>
            <strong style={{ color: c.text, fontWeight: '700' }}>{selectedIds.size} selected</strong>
            {' · '}{filteredFiles.length} filtered of {files.length} total in scope
          </div>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <button style={{ ...s.btnGreen, opacity: (downloadBusy || selectedIds.size === 0) ? 0.5 : 1 }}
              disabled={downloadBusy || selectedIds.size === 0}
              onClick={downloadSelected}>
              ↓ Download selected ({selectedIds.size})
            </button>
            <button style={{ ...s.btnBlue, opacity: (downloadBusy || filteredFiles.length === 0) ? 0.5 : 1 }}
              disabled={downloadBusy || filteredFiles.length === 0}
              onClick={downloadAllFiltered}>
              ↓ Download all filtered ({filteredFiles.length})
            </button>
            <button style={{ ...s.btnAmber, opacity: (downloadBusy || files.length === 0) ? 0.5 : 1 }}
              disabled={downloadBusy || files.length === 0}
              onClick={downloadAllInScope}>
              ↓ Download everything ({files.length})
            </button>
          </div>
          <div style={{ fontSize: '10px', color: c.muted, marginTop: '8px' }}>
            <span style={{ color: '#0F6E56' }}>●</span> Green = checked items &nbsp;·&nbsp;
            <span style={{ color: '#B40000' }}>●</span> Red = filter-aware &nbsp;·&nbsp;
            <span style={{ color: '#303030' }}>●</span> Gray = everything (ignores filters)
          </div>
        </div>

      </div>
    </div>
  );
}
